import { useNavigate } from "react-router-dom";
import { Fragment, SyntheticEvent, useState } from "react";

import {
  Chip,
  Container,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { visuallyHidden } from "@mui/utils";
import ApprovalOutlinedIcon from "@mui/icons-material/ApprovalOutlined";

import conf from "config-dlm";
import Refresh from "components/utils/Refresh";
import usePagination from "hooks/pagination";
import EmptyTableRow from "components/rows/Empty";
import SectionHeader from "components/utils/SectionHeader";
import ProtectedRoute from "components/auth/ProtectedRoute";
import LinearIndeterminate from "components/utils/LinearLoader";
import { usePavmRequestDetail } from "hooks/pavm/requests";
import { a11yProps, CustomTabPanel } from "./Rows";
import { RequestSchema, useUserServiceRequests } from "hooks/user/base";

const ClosedRow = (props: { request: RequestSchema }) => {
  const { id, uri, status, subject, project, approver, requester, updatedAt } =
    props.request;

  const navigate = useNavigate();
  const handleClick = () => navigate(`/requests/closed/${id}`);

  const [openDetail, setOpenDetail] = useState(false);

  const toggleDetail = () => {
    // navigate(`/requests/closed/${id}`);
    setOpenDetail(!openDetail);
  };

  const chip =
    status === "APPROVED" ? (
      <Chip color="success" label={status} onClick={handleClick} />
    ) : (
      <Chip color="error" label={status} onClick={handleClick} />
    );

  return (
    <Fragment>
      {/* div used to default user window to bottom of logs (most recent)
      {autoOpen && <div ref={bottomRef} />} */}

      <TableRow key={id} sx={conf.LAST_CHILD_NO_BORDER}>
        <TableCell>
          <IconButton
            size="small"
            onClick={toggleDetail}
            aria-label="toggle detail row"
            sx={{
              transform: openDetail ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.3s",
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </TableCell>
        <TableCell>{chip}</TableCell>
        <TableCell align="right">{requester ?? "--"}</TableCell>
        <TableCell align="right">{approver ?? "--"}</TableCell>
        <TableCell align="right">{subject ?? "--"}</TableCell>
        <TableCell align="right">
          <b>{project ?? "--"}</b>
        </TableCell>
        <TableCell align="right">
          {new Date(updatedAt).toLocaleString() ?? "--"}
        </TableCell>
      </TableRow>

      {openDetail && <RequestDetail id={id} uri={uri} />}
    </Fragment>
  );
};

const RequestDetail = (props: { id: string; uri: string }) => {
  const { id, uri } = props;
  const detail = usePavmRequestDetail(uri, id);

  return (
    <TableRow
      key={`${id}-detail`}
      sx={{
        bgcolor: (theme) =>
          theme.palette.mode === "light" ? "primary.light" : "primary.dark",
        "&:last-child td, &:last-child th": { border: 0 },
      }}
    >
      {detail.isLoading ? (
        <TableCell colSpan={7}>
          <LinearIndeterminate />
        </TableCell>
      ) : detail.isError ? (
        <TableCell colSpan={7} align="center">
          {detail.error.response?.data.message}
        </TableCell>
      ) : (
        <TableCell colSpan={7}>
          <Box>
            <TextField
              multiline
              fullWidth
              aria-readonly
              margin="normal"
              label="Detail"
              InputProps={{ readOnly: true }}
              defaultValue={detail?.data?.text}
            />

            <TextField
              size="small"
              label="Comment"
              InputProps={{ readOnly: true }}
              defaultValue={detail?.data?.comment}
              fullWidth
              margin="normal"
            />
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
};

export const RequestsClosed = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState(1);

  const paginate = usePagination({ defaultSort: "updatedAt" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const closed = useUserServiceRequests({ status: "CLOSED", ...body });

  const handleChange = (e: SyntheticEvent, value: number) => setValue(value);

  return (
    <ProtectedRoute>
      <Container maxWidth="xl">
        <SectionHeader
          title="Requests"
          icon={<ApprovalOutlinedIcon />}
          buttons={[
            <TablePagination
              key={"requests-closed-pagination"}
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={closed?.data?.meta.total || 0}
              page={page}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
            />,
            <Refresh
              key={"requests-closed-refresh"}
              onClick={closed.refetch}
              isFetching={closed.isFetching}
            />,
          ]}
        />

        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              variant="fullWidth"
              onChange={handleChange}
              textColor="inherit"
              aria-label="request tabs"
              indicatorColor="secondary"
            >
              <Tab
                label="Pending"
                iconPosition="start"
                {...a11yProps(0)}
                onClick={() => navigate("/requests/pending")}
              />

              <Tab label="Closed" iconPosition="start" {...a11yProps(1)} />
            </Tabs>
          </Box>

          <CustomTabPanel value={value} index={1}>
            <TableContainer sx={{ boxShadow: 4, mb: 4, maxHeight: 582 }}>
              <Table aria-label="collapsible table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell />

                    {conf.COLUMNS.REQUESTS.CLOSED.map((column) => (
                      <TableCell
                        key={column.key}
                        sortDirection={order}
                        align={column.isHeader ? "left" : "right"}
                      >
                        <TableSortLabel
                          active={sort === column.key}
                          direction={sort === column.key ? order : "asc"}
                          onClick={(event: React.MouseEvent<unknown>) =>
                            handleSort(event, column.key)
                          }
                        >
                          <b>{column.label}</b>
                          {sort === column.key ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === "desc"
                                ? "sorted descending"
                                : "sorted ascending"}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {closed.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <LinearIndeterminate />
                      </TableCell>
                    </TableRow>
                  ) : closed.data?.data.length === 0 ? (
                    <EmptyTableRow colSpan={7} type="requests" />
                  ) : (
                    closed.data?.data?.map((request: RequestSchema) => (
                      <ClosedRow key={request.id} request={request} />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CustomTabPanel>
        </Box>
      </Container>
    </ProtectedRoute>
  );
};
